import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  buildReportDoc,
  isFullHtmlDoc,
  isHtmlContent,
  markdownToHtml,
} from "@/lib/reportDoc";
import { sanitizeReportDocument } from "@/lib/sanitize";

/*
  A report, readable by anybody holding the link. NO SIGN-IN.

  WHY THIS EXISTS: a report email goes to people who do not necessarily have an
  account — a vendor, a business owner in another function, somebody at an
  entity that does not use this board. Its button used to point at
  /reports?highlight=<id>, which is behind ProtectedRoute, so most recipients
  landed on a login screen for an account they do not have.

  This route is registered OUTSIDE ProtectedRoute in App.js. That is the whole
  mechanism, and it is worth being deliberate about: the page reads one report,
  identified by an unguessable token, through the public view-report edge
  function. It never touches the Supabase client, so it holds no session and
  cannot reach anything else in the database even if the reader is signed in.

  WHY THE FETCH GOES TO THE FUNCTION AND NOT TO POSTGREST: RLS on public.reports
  requires an authenticated role, and no policy could safely admit an anonymous
  read — one permissive enough to allow it at all would also allow LISTING every
  report. The token has to be checked by something holding the service role,
  which is what the function does.

  RENDERING IS THE SAME CODE THE REPORTS PAGE USES — buildReportDoc, REPORT_CSS,
  the same sanitizer, the same sandboxed iframe. A shared report and the in-app
  one are the same document, and a change to the report design cannot land in
  one and miss the other.
*/

// Matches ReportsPage. allow-popups so a link inside a report can still be
// followed; no allow-scripts and no allow-same-origin, so the document cannot
// run anything or reach this page.
const IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";

const FUNCTIONS_BASE = `${process.env.REACT_APP_SUPABASE_URL || ""}/functions/v1`;

const Frame = ({ children }) => (
  <div className="min-h-screen bg-[#EEF1F0] flex items-center justify-center px-4">
    <div className="max-w-lg w-full bg-white border border-border rounded-sm p-8 text-center">
      {children}
    </div>
  </div>
);

export default function PublicReportPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", report: null });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Plain fetch, not the Supabase client: this page must work with no
        // session, and going through the client would attach one if the reader
        // happens to be signed in — which would make the page behave
        // differently for them than for the recipient it was sent to.
        const res = await fetch(`${FUNCTIONS_BASE}/view-report?t=${encodeURIComponent(token || "")}`, {
          headers: { accept: "application/json" },
        });
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: res.status === 404 ? "not_found" : "error", report: null });
          return;
        }
        setState({ status: "ready", report: await res.json() });
      } catch {
        if (!cancelled) setState({ status: "error", report: null });
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const html = useMemo(() => {
    const r = state.report;
    if (!r) return "";
    const content = r.content || "";

    // Three shapes a stored report can take, the same branch ReportsPage uses:
    // a complete HTML document (already carries its own stylesheet), an HTML
    // fragment written against REPORT_CSS's classes, or markdown from before
    // the format changed.
    if (isFullHtmlDoc(content)) return sanitizeReportDocument(content);

    const label = r.scope_name || r.title || "Report";
    const generatedAt = r.generated_at ? new Date(r.generated_at) : null;
    const kind = r.scope_type === "member" ? "Team member" : r.scope_type === "initiative" ? "Initiative" : "Project";

    return sanitizeReportDocument(
      buildReportDoc({
        title: r.title || `${label} — Status Report`,
        eyebrow: "NG-IT Initiative Board",
        subtitle: r.scope_type === "member" ? "Team member progress summary" : "Project status report",
        meta: [
          { label: "Scope", value: kind },
          { label: kind, value: label },
          { label: "Generated", value: generatedAt ? generatedAt.toLocaleString() : "—" },
        ],
        bodyHtml: isHtmlContent(content) ? content : markdownToHtml(content),
        footer: "Source: NG-IT Initiative Board — generated from initiatives, projects, tasks, "
          + "task activity and comments. Read-only snapshot shared by link.",
      }),
    );
  }, [state.report]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-[#EEF1F0] flex items-center justify-center">
        <div className="trk-uppercase-label">Loading report…</div>
      </div>
    );
  }

  if (state.status === "not_found") {
    return (
      <Frame>
        <div className="trk-uppercase-label mb-2">NG-IT Initiative Board</div>
        <h1 className="font-display text-xl font-bold mb-3">This link is not valid</h1>
        <p className="text-sm text-zinc-600">
          The report this link points to could not be found. It may have been deleted, or the link
          may have been broken in delivery — mail clients often wrap a long URL across two lines,
          and only the first half arrives.
        </p>
        <p className="text-sm text-zinc-600 mt-3">
          Ask whoever sent it to share the link again.
        </p>
      </Frame>
    );
  }

  if (state.status === "error") {
    return (
      <Frame>
        <div className="trk-uppercase-label mb-2">NG-IT Initiative Board</div>
        <h1 className="font-display text-xl font-bold mb-3">The report could not be loaded</h1>
        <p className="text-sm text-zinc-600">
          Something went wrong reaching the report. Reloading the page usually clears it.
        </p>
      </Frame>
    );
  }

  return (
    <div className="min-h-screen bg-[#EEF1F0]">
      <iframe
        title="Report"
        sandbox={IFRAME_SANDBOX}
        srcDoc={html}
        data-testid="public-report-frame"
        className="w-full min-h-screen border-0 bg-[#EEF1F0]"
      />
    </div>
  );
}
